import { Component, EventEmitter, Output, TemplateRef, ViewChild } from '@angular/core';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { Project } from '../../models/project.model';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Router } from '@angular/router';
import { UserService } from 'src/app/services/user.service';
import { ProjectService } from 'src/app/services/project.service';
import { AuthService } from 'src/app/services/auth.service';

@Component({
    selector: 'app-assign-project-member',
    templateUrl: './assign-project-member.component.html',
    styleUrl: './assign-project-member.component.scss',
    standalone: false
})
export class AssignProjectMemberComponent {
	@Output() userChanged: EventEmitter<any> = new EventEmitter();

	@ViewChild('assignProjectMember') assignProjectMemberModal!: TemplateRef<any>;
	@ViewChild('removeProjectMember') removeProjectMemberModal!: TemplateRef<any>;
	@ViewChild('successModal') successModal!: TemplateRef<any>;
	@ViewChild('errorModal') errorModal!: TemplateRef<any>;

	assignUserModalRef: any;
	removeAssignUserModalRef: any;
	selectedProject: Project | null = null;
	overview: string | null = null;
	
  currentStep = 0;
  steps = ['Project Select', 'Users Select', 'Summary'];
  stepsFromProject = ['Users Select', 'Summary'];
	
	fromProject = false;
	canBack = true;
	
	projects: Project[] = [];
	selectedUsers: any[] = [];
	removeUser: any = null;
	users: any[] = [];
	loading = true;
	private searchSubject = new Subject<string>();

	currentPage: number = 1;
  pageSize: number = 8;
  totalUsers: number = 0;
  searchText: string = '';
  sortColumn: string = 'createdAt';
  sortDirection: string = 'desc';

	startIndex: number = 0;
  endIndex: number = 0;

	submitted = false;
	spinner = false;
	successMessage = '';
	success = false;

	constructor(private modalService: NgbModal, private router: Router, private authService: AuthService, private userService: UserService, private projectService: ProjectService) {}	
	
	openModal(fromProject: boolean) {
		this.success = false;
		this.submitted = false;
		this.spinner = false;
		this.overview = null;
		this.selectedUsers = [];

		this.selectedProject = null;
		this.currentStep = 0;

		this.subscribeSearchText();

		if (fromProject) {
			this.selectedProject = this.projectService.project;
			this.currentStep = 1;
			this.fromProject = true;
			this.canBack = false;
			this.getUsers(this.selectedProject.id);
		} else {
			this.projectService.getProjectsForAssign().subscribe({
				next: (response: any) => {
					this.projects = response;
				},
				error: (err) => {
					this.authService.toastErrorMessage(err.message ? err.message : err, 'Getting Projects')
				}
			});
		}

		if (this.authService.currentClientValue.plan) {
			this.assignUserModalRef = this.modalService.open(this.assignProjectMemberModal, { size: 'xl', backdrop: 'static', keyboard : false });
		} else {
			this.assignUserModalRef = this.modalService.open(this.errorModal, { size: 'l', backdrop: 'static', keyboard : false });
		}
  }

	openRemoveModal(user: any) {
		this.selectedProject = this.projectService.project;
		this.success = false;
		this.submitted = false;
		this.spinner = false;

		this.removeUser = user;
		this.removeAssignUserModalRef = this.modalService.open(this.removeProjectMemberModal, { size: 'l', backdrop: 'static', keyboard : false });
	}

	subscribeSearchText() {
		// Subscribe to search input changes with debounce
		this.searchSubject.pipe(
			debounceTime(300), // Adjust debounce time as needed
			distinctUntilChanged()
		).subscribe(() => {
			this.currentPage = 1; // Reset to first page when search text changes
			if (this.selectedProject) {
				this.getUsers(this.selectedProject.id);
			}
		});
	}
  
  nextStep(): void {
		this.submitted = true;

		if (this.selectedProject) {
			if (this.currentStep === 0) {
				this.getUsers(this.selectedProject.id);
				this.currentStep++;
				this.submitted = false;
			}

			if (this.currentStep === 1) {
				if (this.selectedUsers.length > 0) {
					this.currentStep++;
					this.canBack = true;
					this.submitted = false;
				}
			}
		}
  }

  backStep(): void {
    if (this.currentStep > 0) {
			if (this.fromProject && this.currentStep-1 === 1) {
				this.canBack = false;
			}

			this.currentStep--;
			this.submitted = false;
    }
  }

	onSubmit() {
		this.submitted = true;
		this.spinner = true;
		
		if (!this.selectedProject) {
			this.submitted = false;
			this.spinner = false;
			this.currentStep = 0;
			return;
		}
		
		const selectedUserIds: any[] = this.selectedUsers.map(user => user.id);

		this.projectService.assignProjectMembers(this.selectedProject.id, { client_users: selectedUserIds }).subscribe({
			next: () => {
				this.submitted = false;
				this.spinner = false;
				this.assignUserModalRef.close();
				this.modalService.open(this.successModal, { size: 'l', backdrop: 'static', keyboard : false });
				this.successMessage = 'assigned';
				this.success = true;
			},
			error: (err) =>{
				this.success = false;
				this.submitted = false;
				this.spinner = false;
				this.authService.toastErrorMessage(err.message ? err.message : err , 'Assign Project Member!');
			}
		});
	}

	onRemoveSubmit() {
		this.submitted = true;
		this.spinner = true;
		
		if (!this.selectedProject) {
			this.submitted = false;
			this.spinner = false;
			this.currentStep = 0;
			return;
		}
		
		this.projectService.removeProjectMember(this.selectedProject.id, { client_user: this.removeUser.id }).subscribe({
			next: () => {
				this.submitted = false;
				this.spinner = false;
				this.removeAssignUserModalRef.close();
				this.modalService.open(this.successModal, { size: 'l', backdrop: 'static', keyboard : false });
				this.successMessage = 'removed';
				this.success = true;
			},
			error: (err) =>{
				this.success = false;
				this.submitted = false;
				this.spinner = false;
				this.authService.toastErrorMessage(err.message ? err.message : err , 'Remove Project Member!');
			}
		});
	}

	addRemoveMembers(data: any) {
    if (data.state) {
      this.selectedUsers.push(data)
    } else {
      const index = this.selectedUsers.indexOf(data);
      if (index !== -1) {
        this.selectedUsers.splice(index, 1);
      }
    }
  }

	getUsers(project_id: number) {
		this.loading = true;
    const params = {
      page: this.currentPage.toString(),
      pageSize: this.pageSize.toString(),
      searchText: this.searchText,
      sortColumn: this.sortColumn,
      sortDirection: this.sortDirection
    };

		this.userService.getClientUsersForAssign(project_id, params).subscribe({
			next: (response: any) => {
				this.users = response.users;
				this.totalUsers = response.totalUsers;
				this.calculateIndexes();
				this.loading = false;
			},
			error: (err) => {
				this.authService.toastErrorMessage(err.message ? err.message : err, 'Getting Users')
			}
		});
  }

  onSearch() {
    this.searchSubject.next(this.searchText);
  }

	onPageChange(page: number) {
    this.currentPage = page;
    if (this.selectedProject) {
			this.getUsers(this.selectedProject.id);
		}
  }

  onSort(column: string) {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }

		if (this.selectedProject) {
			this.getUsers(this.selectedProject.id);
		}
  }

	calculateIndexes() {
    this.startIndex = (this.currentPage - 1) * this.pageSize + 1;
    this.endIndex = Math.min(this.currentPage * this.pageSize, this.totalUsers);
  }

	closeModal() {
		if (this.authService.currentClientValue.plan) {
			if (this.router.url.includes('team')) {
				if (this.success) {
					if (this.projectService.project.id.toString() === this.selectedProject?.id.toString()) {
						this.userChanged.emit();
					}
				}
			}
	
			this.modalService.dismissAll();
		} else {
			this.modalService.dismissAll();
		}
	}
}
